/**
 * Browser WHIP (publish) and WHEP (play) against the relay's MediaMTX, both authorized with a bearer token (an Auth0
 * JWT, or the participant token a room phone got from its QR pass). TURN credentials come from the session state so
 * strict networks relay through coturn.
 */
import { http } from "./api";
export interface Turn {
  urls: string[];
  username: string;
  credential: string;
}

async function negotiate(pc: RTCPeerConnection, url: string, token: string): Promise<string | null> {
  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  await new Promise<void>((resolve) => {
    if (pc.iceGatheringState === "complete") return resolve();
    const t = setTimeout(resolve, 1500);
    pc.addEventListener("icegatheringstatechange", () => {
      if (pc.iceGatheringState === "complete") {
        clearTimeout(t);
        resolve();
      }
    });
  });
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/sdp", Authorization: `Bearer ${token}` },
    body: pc.localDescription!.sdp,
  });
  if (!res.ok) throw new Error(res.status === 404 ? "not live" : `${res.status}: ${(await res.text()).slice(0, 200)}`);
  const location = res.headers.get("Location");
  await pc.setRemoteDescription({ type: "answer", sdp: await res.text() });
  return location ? new URL(location, url).toString() : null;
}

export interface Connection {
  pc: RTCPeerConnection;
  close: () => void;
}

function closer(pc: RTCPeerConnection, resource: string | null, token: string) {
  return () => {
    if (resource)
      fetch(resource, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }).catch(() => undefined);
    pc.close();
  };
}

/**
 * Once a minute, report how an instrument stream actually plays in this browser (frame rate shown, drops, freezes,
 * jitter buffer, decode time, round trip) so Jupiter can show measured smoothness and delay. Only instrument streams
 * (/edge/ URLs). Returns a stop function.
 */
export function reportStreamStats(pc: RTCPeerConnection, whepUrl: string, token: string): () => void {
  if (!/\/edge\//.test(whepUrl)) return () => undefined;
  let prev: {
    jb: number;
    jbn: number;
    dec: number;
    decn: number;
    dropped: number;
    freezes: number;
    freezeS: number;
  } | null = null;
  const sample = async () => {
    if (pc.connectionState !== "connected") return;
    const stats = await pc.getStats().catch(() => null);
    if (!stats) return;
    let v: Record<string, number> | null = null;
    let rtt: number | undefined;
    stats.forEach((r) => {
      const x = r as unknown as Record<string, number | string | boolean>;
      if (r.type === "inbound-rtp" && x.kind === "video") v = x as Record<string, number>;
      if (
        r.type === "candidate-pair" &&
        x.state === "succeeded" &&
        x.nominated &&
        typeof x.currentRoundTripTime === "number"
      )
        rtt = x.currentRoundTripTime * 1000;
    });
    if (!v) return;
    const s = v as Record<string, number>;
    const cur = {
      jb: s.jitterBufferDelay ?? 0,
      jbn: s.jitterBufferEmittedCount ?? 0,
      dec: s.totalDecodeTime ?? 0,
      decn: s.framesDecoded ?? 0,
      dropped: s.framesDropped ?? 0,
      freezes: s.freezeCount ?? 0,
      freezeS: s.totalFreezesDuration ?? 0,
    };
    const p = prev;
    prev = cur;
    if (!p) return; // the first sample only sets the baseline; numbers are per minute
    const per = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 1000) : undefined);
    await http
      .post(token, "/stream-stats", {
        whepUrl,
        fps: typeof s.framesPerSecond === "number" ? Math.round(s.framesPerSecond * 10) / 10 : undefined,
        framesDecoded: cur.decn - p.decn,
        framesDropped: cur.dropped - p.dropped,
        freezeCount: cur.freezes - p.freezes,
        freezeMs: Math.round((cur.freezeS - p.freezeS) * 1000),
        jitterBufferMs: per(cur.jb - p.jb, cur.jbn - p.jbn),
        decodeMs: per(cur.dec - p.dec, cur.decn - p.decn),
        rttMs: rtt !== undefined ? Math.round(rtt) : undefined,
        width: s.frameWidth,
        height: s.frameHeight,
      })
      .catch(() => undefined);
  };
  const first = setTimeout(sample, 5_000);
  const every = setInterval(sample, 60_000);
  return () => {
    clearTimeout(first);
    clearInterval(every);
  };
}

/** Play a stream. `audio: false` for the instrument (it has no audio). */
export async function whepPlay(
  url: string,
  token: string,
  turn: Turn,
  opts: { audio: boolean; video: boolean },
  onStream: (s: MediaStream) => void
): Promise<Connection> {
  const pc = new RTCPeerConnection({
    iceServers: [{ urls: turn.urls, username: turn.username, credential: turn.credential }],
  });
  if (opts.video) pc.addTransceiver("video", { direction: "recvonly" });
  if (opts.audio) pc.addTransceiver("audio", { direction: "recvonly" });
  const stream = new MediaStream();
  pc.ontrack = (ev) => {
    stream.addTrack(ev.track);
    onStream(stream);
  };
  try {
    const resource = await negotiate(pc, url, token);
    const stopStats = reportStreamStats(pc, url, token);
    const close = closer(pc, resource, token);
    return {
      pc,
      close: () => {
        stopStats();
        close();
      },
    };
  } catch (e) {
    pc.close();
    throw e;
  }
}

/**
 * Prefer H.264 for sending video. Browsers default to VP8, which the relay's MP4 recorder cannot store (it records
 * only the audio of a VP8 publisher), so without this a participant's camera never reaches the session recording.
 * H.264 is available in Chrome, Safari, Firefox and Android; a browser without it falls back to its default codec.
 */
function preferH264(transceiver: RTCRtpTransceiver) {
  const caps =
    typeof RTCRtpSender !== "undefined" && RTCRtpSender.getCapabilities ? RTCRtpSender.getCapabilities("video") : null;
  if (!caps || !transceiver.setCodecPreferences) return;
  const h264 = caps.codecs.filter((c) => c.mimeType.toLowerCase() === "video/h264");
  if (!h264.length) return;
  // constrained baseline / packetization-mode=1 first: the most widely decodable profile
  h264.sort(
    (a, b) => Number(/42e01f|42001f/i.test(b.sdpFmtpLine ?? "")) - Number(/42e01f|42001f/i.test(a.sdpFmtpLine ?? ""))
  );
  const rest = caps.codecs.filter((c) => c.mimeType.toLowerCase() !== "video/h264");
  try {
    transceiver.setCodecPreferences([...h264, ...rest]);
  } catch {
    /* keep the browser default */
  }
}

/** Publish the local camera/mic. Muting is done on the tracks (track.enabled), so the connection never renegotiates. */
export async function whipPublish(url: string, token: string, turn: Turn, local: MediaStream): Promise<Connection> {
  const pc = new RTCPeerConnection({
    iceServers: [{ urls: turn.urls, username: turn.username, credential: turn.credential }],
  });
  for (const track of local.getTracks()) {
    const t = pc.addTransceiver(track, { direction: "sendonly" });
    if (track.kind === "video") preferH264(t);
  }
  try {
    const resource = await negotiate(pc, url, token);
    return { pc, close: closer(pc, resource, token) };
  } catch (e) {
    pc.close();
    throw e;
  }
}

/** Camera + mic with echo cancellation; falls back to mic only when there is no camera or it is refused. */
export async function getLocalMedia(): Promise<MediaStream> {
  const audio = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio,
      video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" },
    });
  } catch {
    return navigator.mediaDevices.getUserMedia({ audio, video: false });
  }
}
